import { createContext, useCallback, useContext, useRef, useState } from "react";
import { WarningCircle, X } from "@phosphor-icons/react";

const ConfirmContext = createContext(null);

/**
 * Elegant confirmation dialog with Navy/Gold theme.
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, confirmText, tone });
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: "تأكيد",
    message: "هل أنت متأكد؟",
    confirmText: "تأكيد",
    cancelText: "إلغاء",
    tone: "danger", // "danger" | "primary" | "warning"
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || "تأكيد",
        message: opts.message || "هل أنت متأكد؟",
        confirmText: opts.confirmText || "تأكيد",
        cancelText: opts.cancelText || "إلغاء",
        tone: opts.tone || "danger",
      });
    });
  }, []);

  const close = (value) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  const toneBtn =
    state.tone === "danger"
      ? "bg-destructive hover:bg-destructive/90 text-white"
      : state.tone === "warning"
      ? "bg-[#F57F17] hover:bg-[#F57F17]/90 text-white"
      : "bg-primary hover:bg-[#E1C484] text-primary-foreground";

  const toneIcon =
    state.tone === "danger" ? "text-destructive" : state.tone === "warning" ? "text-[#F57F17]" : "text-primary";
  const toneRing =
    state.tone === "danger" ? "bg-destructive/15 border-destructive/40" : state.tone === "warning" ? "bg-[#F57F17]/15 border-[#F57F17]/40" : "bg-primary/15 border-primary/40";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(10px)" }}
          onClick={() => close(false)}
          dir="rtl"
          data-testid="confirm-dialog"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-card border border-primary/25 rounded-2xl w-full max-w-md gold-glow overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            style={{ animation: "confirmPop 0.22s ease-out" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-5 border-b border-primary/15 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${toneRing}`}>
                  <WarningCircle size={22} weight="fill" className={toneIcon} />
                </div>
                <div>
                  <h3 className="text-primary font-bold text-lg leading-tight">{state.title}</h3>
                </div>
              </div>
              <button
                onClick={() => close(false)}
                className="text-white/50 hover:text-white p-1 rounded"
                aria-label="إغلاق"
                data-testid="confirm-close"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">{state.message}</p>
            </div>

            {/* Actions */}
            <div className="p-5 pt-0 flex gap-3">
              <button
                onClick={() => close(true)}
                data-testid="confirm-yes"
                className={`flex-1 py-3 rounded-xl font-bold transition-all ${toneBtn}`}
              >
                {state.confirmText}
              </button>
              <button
                onClick={() => close(false)}
                data-testid="confirm-no"
                className="px-6 py-3 rounded-xl border border-white/20 text-white/80 hover:bg-white/5 transition-all"
              >
                {state.cancelText}
              </button>
            </div>
          </div>

          <style>{`
            @keyframes confirmPop {
              0%   { opacity: 0; transform: scale(0.94) translateY(6px); }
              100% { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
